import { Switch as HeadSwitch } from '@headlessui/react';
import { clsx } from 'clsx';
import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import type { FormItemProps } from '../../blocks/FormItem';
import FormItem from '../../blocks/FormItem';
import Tooltip from '../Tooltip';

export type SwitchProps = Pick<FormItemProps, 'label' | 'labelWeight'> & {
    name?: string;
    value: boolean;
    onChange?: (value: boolean) => void;
    description?: ReactNode;
    helpElement?: ReactNode;
    required?: boolean;
    sideLabelPosition?: 'left' | 'right';
    sideLabel?: ReactNode;
    sideLabelColor?: 'default' | 'green' | 'red';
    /** Font size for side label. Defaults to 'sm' (14px) */
    sideLabelSize?: 'sm' | 'base';
    /** Content shown in a tooltip via an info icon next to the switch */
    tooltipElement?: ReactNode;
    disabled?: boolean;
};

const Switch = ({
    label,
    value,
    onChange,
    description,
    helpElement,
    name,
    sideLabel,
    sideLabelPosition = 'right',
    sideLabelColor = 'default',
    sideLabelSize = 'sm',
    tooltipElement,
    labelWeight,
    disabled,
    required,
}: SwitchProps) => {
    const labelColorClass =
        sideLabelColor === 'green' ? 'text-green-600' : sideLabelColor === 'red' ? 'text-red-600' : 'text-neutral-800';
    const sideLabelTextSize = sideLabelSize === 'base' ? 'text-base' : 'text-sm';

    const switchElement = (
        <HeadSwitch
            checked={value}
            className={clsx(
                `group relative inline-flex h-6 w-11 focus:ring-teal-600 data-[checked]:bg-teal-600`,
                `flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-neutral-300 transition-colors`,
                `duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none`,
                `data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50`
            )}
            data-cy="switchField"
            disabled={disabled}
            onChange={onChange}
        >
            <span className="sr-only">Use setting</span>
            <span
                aria-hidden="true"
                className={clsx(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow',
                    'ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5'
                )}
            />
        </HeadSwitch>
    );

    const labelWithTooltip = tooltipElement ? (
        <span className="inline-flex items-center gap-1">
            {label}
            <Tooltip title={tooltipElement}>
                <Info className="h-4 w-4 cursor-pointer text-neutral-400" />
            </Tooltip>
        </span>
    ) : (
        label
    );

    return (
        <FormItem dataInputName={name} label={labelWithTooltip} required={required} labelWeight={labelWeight}>
            <div className="flex flex-col gap-1.5">
                {description && <span className="text-base font-medium text-neutral-500">{description}</span>}
                <div className="flex items-center space-x-1">
                    {sideLabelPosition === 'left' && (
                        <span className={clsx('font-medium', sideLabelTextSize, labelColorClass, 'mr-[10px]')}>
                            {sideLabel}
                        </span>
                    )}
                    {switchElement}
                    {sideLabelPosition === 'right' && (
                        <span className={clsx('font-medium', sideLabelTextSize, labelColorClass, 'ml-[10px]')}>
                            {sideLabel}
                        </span>
                    )}
                </div>
            </div>
            {helpElement && <p className="text-muted-foreground mt-2 text-sm">{helpElement}</p>}
        </FormItem>
    );
};
export default Switch;
